// v6 — five named preset cards above the controls form.
//
// Each preset is a complete `Parameters` object so clicking it replaces
// the entire state in one shot. The presets are described in plain
// language (the default) so mum doesn't have to read parameter jargon
// to pick one. In technical mode, the card surfaces how many fields
// each preset overrides ("15 params overridden") so Ty can audit at a
// glance.
//
// Preset names + descriptions are kept here as data so they're easy to
// tweak later. The Parameters objects are derived from
// DEFAULT_PARAMETERS with explicit overrides — no hidden magic.
//
// v6-polish:
//   - Clicking a preset surfaces a top-right toast with both the preset
//     name (headline) and its description (detail line). 2500ms.
//   - aria-keyshortcuts="1".."5" so screen-reader users hear the hint.
//   - Pressing 1-5 anywhere in the panel activates the corresponding
//     preset. The listener is scoped to the panel and ignores key
//     events that originate from form fields so typing "1" in the
//     catchlight slider doesn't fire a preset swap.

import { useEffect, useMemo, useRef } from 'react';
import { DEFAULT_PARAMETERS, type Parameters } from '../types/parameters';
import { pick, useLanguage } from '../lib/language';
import { useToast } from './Toast';

// ─── Per-preset data ──────────────────────────────────────────────────

export interface Preset {
  /** Stable id used for keys + aria-labelledby. */
  id: string;
  /** Plain-language display name. */
  name: string;
  /** One-line plain description. */
  description: string;
  /** Full Parameters object. */
  params: Parameters;
  /** Optional keybinding (1..9). Lets users drive the bar from the keyboard. */
  shortcut?: string;
}

// 1. Dramatic
const DRAMATIC: Preset = {
  id: 'dramatic',
  name: 'Dramatic',
  description:
    'Bold colours, dramatic light — high contrast and moody.',
  shortcut: '1',
  params: {
    ...DEFAULT_PARAMETERS,
    iris_color: '#a23e2a',
    iris_detail: 0.8,
    iris_pattern: 'mixed',
    limbal_ring: { enabled: true, intensity: 0.85 },
    pupil_size: 0.5,
    pupil_shape: 'round',
    sclera_tint: '#f4ece0',
    sclera_veining: 0.25,
    eyelid_shape: 'half',
    lid_shadow: 0.8,
    lash_density: 0.8,
    lash_length: 0.7,
    lash_curve: 'dramatic',
    catchlight: {
      ...DEFAULT_PARAMETERS.catchlight,
      enabled: true,
      x: 0.3,
      y: 0.3,
      size: 0.4,
      shape: 'round',
      color: '#ffe8b0',
      intensity: 1,
      blur: 0.1,
      count: 1,
      count_pattern: 'single',
    },
    reflected_scene: { ...DEFAULT_PARAMETERS.reflected_scene },
    color_tint: {
      ...DEFAULT_PARAMETERS.color_tint,
      enabled: true,
      color: '#c93c20',
      intensity: 0.25,
    },
    emotion: 'alert',
    gaze_direction: 'centre',
    lighting: {
      enabled: true,
      key_angle: 'side-left',
      quality: 'hard',
      temperature: 'warm',
      intensity: 0.8,
    },
    aspect_ratio: '4:3',
    resolution: '2K',
    n_variations: 1,
    skin_visibility: 0.4,
    bokeh_strength: 0.5,
  },
};

// 2. Soft romantic
const SOFT_ROMANTIC: Preset = {
  id: 'soft-romantic',
  name: 'Soft romantic',
  description:
    'Pastel colours, gentle light — tender portraits and book illustrations.',
  shortcut: '2',
  params: {
    ...DEFAULT_PARAMETERS,
    iris_color: '#c8a4d6',
    iris_detail: 0.3,
    iris_pattern: 'radial',
    limbal_ring: { enabled: true, intensity: 0.4 },
    pupil_size: 0.55,
    pupil_shape: 'round',
    sclera_tint: '#fff2f5',
    sclera_veining: 0.15,
    eyelid_shape: 'open',
    lid_shadow: 0.3,
    lash_density: 0.6,
    lash_length: 0.7,
    lash_curve: 'subtle',
    catchlight: {
      ...DEFAULT_PARAMETERS.catchlight,
      enabled: false,
    },
    reflected_scene: { ...DEFAULT_PARAMETERS.reflected_scene },
    color_tint: {
      ...DEFAULT_PARAMETERS.color_tint,
      enabled: true,
      color: '#ffd5e2',
      intensity: 0.15,
    },
    emotion: 'peaceful',
    gaze_direction: 'centre',
    lighting: {
      enabled: true,
      key_angle: 'front-left',
      quality: 'soft',
      temperature: 'warm',
      intensity: 0.4,
    },
    aspect_ratio: '4:3',
    resolution: '2K',
    n_variations: 1,
    skin_visibility: 0.55,
    bokeh_strength: 0.7,
  },
};

// 3. Realistic portrait
const REALISTIC_PORTRAIT: Preset = {
  id: 'realistic-portrait',
  name: 'Realistic portrait',
  description:
    'Natural colours, neutral light — for lifelike reference drawings.',
  shortcut: '3',
  params: {
    ...DEFAULT_PARAMETERS,
    iris_color: '#5a6e4a',
    iris_detail: 0.55,
    iris_pattern: 'radial',
    limbal_ring: { enabled: true, intensity: 0.5 },
    pupil_size: 0.4,
    pupil_shape: 'round',
    sclera_tint: '#f4ece0',
    sclera_veining: 0.3,
    eyelid_shape: 'open',
    lid_shadow: 0.4,
    lash_density: 0.5,
    lash_length: 0.5,
    lash_curve: 'subtle',
    catchlight: {
      ...DEFAULT_PARAMETERS.catchlight,
      enabled: false,
    },
    reflected_scene: { ...DEFAULT_PARAMETERS.reflected_scene },
    color_tint: { ...DEFAULT_PARAMETERS.color_tint, enabled: false },
    emotion: 'neutral',
    gaze_direction: 'centre',
    lighting: {
      enabled: true,
      key_angle: 'front-left',
      quality: 'soft',
      temperature: 'neutral',
      intensity: 0.5,
    },
    aspect_ratio: '4:3',
    resolution: '2K',
    n_variations: 1,
    skin_visibility: 0.5,
    bokeh_strength: 0.4,
  },
};

// 4. Anime cel
const ANIME_CEL: Preset = {
  id: 'anime-cel',
  name: 'Anime cel',
  description:
    'Bright saturated eyes with a round sparkle — for anime / cel-shaded style.',
  shortcut: '4',
  params: {
    ...DEFAULT_PARAMETERS,
    iris_color: '#5fb6ff',
    iris_detail: 0.2,
    iris_pattern: 'radial',
    limbal_ring: { enabled: true, intensity: 0.9 },
    pupil_size: 0.45,
    pupil_shape: 'round',
    sclera_tint: '#ffffff',
    sclera_veining: 0,
    eyelid_shape: 'open',
    lid_shadow: 0.15,
    lash_density: 0.85,
    lash_length: 0.85,
    lash_curve: 'dramatic',
    catchlight: {
      ...DEFAULT_PARAMETERS.catchlight,
      enabled: true,
      x: 0.35,
      y: 0.3,
      size: 0.3,
      shape: 'round',
      color: '#ffffff',
      intensity: 1,
      blur: 0,
      count: 2,
      count_pattern: 'paired',
    },
    reflected_scene: { ...DEFAULT_PARAMETERS.reflected_scene },
    color_tint: {
      ...DEFAULT_PARAMETERS.color_tint,
      enabled: true,
      color: '#a4d4ff',
      intensity: 0.1,
    },
    emotion: 'surprised',
    gaze_direction: 'centre',
    lighting: {
      enabled: true,
      key_angle: 'above',
      quality: 'soft',
      temperature: 'neutral',
      intensity: 0.25,
    },
    aspect_ratio: '1:1',
    resolution: '2K',
    n_variations: 1,
    skin_visibility: 0.3,
    bokeh_strength: 0.2,
  },
};

// 5. Inked comic
const INKED_COMIC: Preset = {
  id: 'inked-comic',
  name: 'Inked comic',
  description:
    'Black-and-white inked look with heavy lashes — for graphic-novel art.',
  shortcut: '5',
  params: {
    ...DEFAULT_PARAMETERS,
    iris_color: '#1a1a1a',
    iris_detail: 0.7,
    iris_pattern: 'mixed',
    limbal_ring: { enabled: true, intensity: 1 },
    pupil_size: 0.45,
    pupil_shape: 'round',
    sclera_tint: '#f8f4ec',
    sclera_veining: 0,
    eyelid_shape: 'half',
    lid_shadow: 0.85,
    lash_density: 0.95,
    lash_length: 0.7,
    lash_curve: 'dramatic',
    catchlight: {
      ...DEFAULT_PARAMETERS.catchlight,
      enabled: true,
      x: 0.35,
      y: 0.3,
      size: 0.15,
      shape: 'round',
      color: '#ffffff',
      intensity: 1,
      blur: 0,
      count: 1,
      count_pattern: 'single',
    },
    reflected_scene: { ...DEFAULT_PARAMETERS.reflected_scene },
    color_tint: {
      enabled: true,
      color: '#808080',
      intensity: 0,
    },
    emotion: 'menacing',
    gaze_direction: 'centre',
    lighting: {
      enabled: true,
      key_angle: 'side-right',
      quality: 'hard',
      temperature: 'neutral',
      intensity: 0.7,
    },
    aspect_ratio: '4:3',
    resolution: '2K',
    n_variations: 1,
    skin_visibility: 0.5,
    bokeh_strength: 0.3,
  },
};

export const PRESETS: Preset[] = [
  DRAMATIC,
  SOFT_ROMANTIC,
  REALISTIC_PORTRAIT,
  ANIME_CEL,
  INKED_COMIC,
];

// ─── Diff helper ──────────────────────────────────────────────────────

/**
 * Count how many top-level + nested Parameters fields the preset
 * overrides vs `DEFAULT_PARAMETERS`. Used to show "(N params overridden)"
 * in technical mode for Ty's audit-style review.
 */
function countOverrides(preset: Parameters, base: Parameters): number {
  let n = 0;
  const keys = Object.keys(base) as (keyof Parameters)[];
  for (const k of keys) {
    const a = preset[k];
    const b = base[k];
    if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
      const aKeys = Object.keys(a as object);
      for (const sk of aKeys) {
        const av = (a as unknown as Record<string, unknown>)[sk];
        const bv = (b as unknown as Record<string, unknown>)[sk];
        if (av !== bv) n += 1;
      }
    } else if (a !== b) {
      n += 1;
    }
  }
  return n;
}

// ─── Component ────────────────────────────────────────────────────────

export interface PresetBarProps {
  /** Replace the entire Parameters object. */
  onApply: (next: Parameters) => void;
  /** Disabled while a chat request is in flight. */
  disabled?: boolean;
}

const TOAST_DURATION_MS = 2500;

export function PresetBar(props: PresetBarProps) {
  const { onApply, disabled = false } = props;
  const { lang } = useLanguage();
  const toast = useToast();
  const containerRef = useRef<HTMLFieldSetElement | null>(null);

  const overrides = useMemo(
    () => PRESETS.map((p) => countOverrides(p.params, DEFAULT_PARAMETERS)),
    [],
  );

  const titleText = pick(lang, {
    plain: 'Start with a preset',
    technical: 'Preset configurations',
  });
  const subtitleText = pick(lang, {
    plain: 'Pick a starting look — you can fine-tune after.',
    technical:
      'Replace the entire Parameters object with a pre-defined configuration.',
  });
  const overrideSuffix = pick(lang, {
    plain: null as string | null,
    technical: 'params overridden',
  });

  const apply = (preset: Preset) => {
    onApply(preset.params);
    // v6-polish — top-right toast with the preset name as headline and
    // the description as the second line. 2500ms — long enough to read
    // both lines, short enough to not stack up if the user spams clicks.
    toast.push('success', `Applied: ${preset.name}`, {
      detail: preset.description,
      durationMs: TOAST_DURATION_MS,
    });
  };

  // v6-polish — keyboard shortcuts 1-5 to activate the matching preset.
  // Scoped to the preset bar container via the ref so typing "1" into a
  // slider or chat textarea doesn't hijack the keystroke. Modifier
  // keys are required (Alt) so the shortcut never fires by accident on
  // numeric input — Alt+1..5 is also a familiar convention from tab
  // switching.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const handler = (e: KeyboardEvent) => {
      // Only react to Alt+digit. Holding Alt keeps normal typing intact.
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const match = PRESETS.find((p) => p.shortcut === e.key);
      if (!match) return;
      e.preventDefault();
      if (disabled) return;
      apply(match);
    };
    node.addEventListener('keydown', handler);
    return () => node.removeEventListener('keydown', handler);
    // `apply` and `disabled` are stable in practice (apply closes over
    // `props.onApply`, `toast.push`) — but re-bind on changes so we
    // never call a stale closure.
  }, [disabled, toast, onApply]);

  return (
    <fieldset
      ref={containerRef}
      aria-label="Start with a preset"
      className="flex flex-col gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 pb-3 pt-3 dark:border-neutral-800 dark:bg-neutral-950"
    >
      <legend className="sr-only">{titleText}</legend>
      <div className="flex flex-col gap-0.5 px-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400">
            {titleText}
          </span>
          <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
            Press <kbd className="rounded border border-neutral-300 px-1 font-mono dark:border-neutral-600">Alt</kbd>+<kbd className="rounded border border-neutral-300 px-1 font-mono dark:border-neutral-600">1</kbd>–<kbd className="rounded border border-neutral-300 px-1 font-mono dark:border-neutral-600">5</kbd>
          </span>
        </div>
        <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
          {subtitleText}
        </span>
      </div>
      <div
        role="radiogroup"
        aria-label={titleText}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5"
      >
        {PRESETS.map((p, idx) => {
          const count = overrides[idx];
          const labelId = `preset-${p.id}-label`;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={false}
              aria-labelledby={labelId}
              aria-keyshortcuts={p.shortcut ?? undefined}
              aria-describedby={`${labelId}-desc`}
              disabled={disabled}
              onClick={() => apply(p)}
              className={[
                'group flex h-full min-h-[64px] cursor-pointer flex-col items-start gap-1 rounded-md border bg-white p-3 text-left motion-safe:transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-950',
                'border-neutral-300 hover:border-accent hover:bg-accent/5 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:border-accent dark:hover:bg-accent/10',
                'disabled:cursor-not-allowed disabled:opacity-60',
              ].join(' ')}
            >
              <span
                id={labelId}
                className="inline-flex w-full items-baseline justify-between gap-2"
              >
                <span className="text-sm font-semibold text-neutral-900 group-hover:text-accent dark:text-neutral-50 dark:group-hover:text-accent">
                  {p.name}
                </span>
                {p.shortcut ? (
                  <kbd
                    aria-hidden="true"
                    className="rounded border border-neutral-300 px-1 font-mono text-[10px] text-neutral-500 group-hover:border-accent group-hover:text-accent dark:border-neutral-700 dark:text-neutral-400"
                  >
                    Alt+{p.shortcut}
                  </kbd>
                ) : null}
              </span>
              <span
                id={`${labelId}-desc`}
                className="text-[11px] leading-snug text-neutral-600 dark:text-neutral-400"
              >
                {p.description}
                {overrideSuffix ? (
                  <>
                    {' '}
                    <span className="font-mono text-[10px] text-neutral-500 dark:text-neutral-400">
                      ({count} {overrideSuffix})
                    </span>
                  </>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}